  import React, { useRef, useState } from "react";
  import { Toast } from "primereact/toast";
  import { useFormik } from "formik";
  import * as Yup from "yup";
  import { InputText } from "primereact/inputtext";
  import { Dropdown } from "primereact/dropdown";
  import { Button } from "primereact/button";
  import { Card } from "primereact/card";
  import { Message } from "primereact/message";
  import { DataTable } from "primereact/datatable";
  import { Column } from "primereact/column";

  type WithdrawalType = "bank" | "wallet";

  interface WithdrawalMethod {
    type: WithdrawalType;
    details: any;
  }

  // ...imports inchangés

  const AddWithdrawalInfo: React.FC = () => {
    const toast = useRef<Toast>(null);
    const [submitting, setSubmitting] = useState(false);
    const [selectedType, setSelectedType] = useState<WithdrawalType>("bank");
    const [withdrawals, setWithdrawals] = useState<WithdrawalMethod[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const formik = useFormik({
      initialValues: {
        accountName: "",
        accountNumber: "",
        bankName: "",
        walletProvider: "",
        walletNumber: "",
      },
      validationSchema: Yup.object().shape({
        ...(selectedType === "bank"
          ? {
              accountName: Yup.string().required("Account holder name is required."),
              accountNumber: Yup.string().required("Account number is required."),
              bankName: Yup.string().required("Bank name is required."),
            }
          : {
              walletProvider: Yup.string().required("Provider is required."),
              walletNumber: Yup.string().required("Wallet number is required."),
            }),
      }),
      onSubmit: (values) => {
        setSubmitting(true);

        const method: WithdrawalMethod =
          selectedType === "bank"
            ? {
                type: "bank",
                details: {
                  accountName: values.accountName,
                  accountNumber: values.accountNumber,
                  bankName: values.bankName,
                },
              }
            : {
                type: "wallet",
                details: {
                  walletProvider: values.walletProvider,
                  walletNumber: values.walletNumber,
                },
              };

        if (editingIndex !== null) {
          const updated = [...withdrawals];
          updated[editingIndex] = method;
          setWithdrawals(updated);
          setEditingIndex(null);

          toast.current?.show({
            severity: "info",
            summary: "Updated",
            detail: "Withdrawal method updated successfully.",
          });
        } else {
          setWithdrawals((prev) => [...prev, method]);
          toast.current?.show({
            severity: "success",
            summary: "Added",
            detail: "Withdrawal method added successfully.",
          });
        }

        formik.resetForm();
        setSubmitting(false);
      },
    });

    const handleEdit = (index: number) => {
      const method = withdrawals[index];
      setSelectedType(method.type);
      if (method.type === "bank") {
        formik.setValues({
          accountName: method.details.accountName,
          accountNumber: method.details.accountNumber,
          bankName: method.details.bankName,
          walletProvider: "",
          walletNumber: "",
        });
      } else {
        formik.setValues({
          accountName: "",
          accountNumber: "",
          bankName: "",
          walletProvider: method.details.walletProvider,
          walletNumber: method.details.walletNumber,
        });
      }
      setEditingIndex(index);
    };

    const typeOptions = [
      { label: "Bank Account", value: "bank" },
      { label: "Wallet (Mobile Money, PayPal...)", value: "wallet" },
    ];

    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Toast ref={toast} />

        <div className="grid md:grid-cols-2 gap-6">
          {/* Table */}
          <Card className="shadow-lg border border-gray-200">
            <h3 className="text-2xl font-semibold mb-6 text-gray-900">Withdrawal Accounts</h3>
            {withdrawals.length === 0 ? (
              <p className="text-gray-500">No Account added yet.</p>
            ) : (
              <DataTable
                value={withdrawals}
                className="p-datatable-sm"
                stripedRows
                rowHover
              >
              <Column
                field="type"
                header="Type"
                body={(rowData) => (
                  <span className="capitalize font-medium text-gray-800">
                    {rowData.type === "bank" ? "Bank Account" : "Wallet"}
                  </span>
                )}
              />

              <Column
                header="Details"
                body={(rowData, { rowIndex }) => (
                  <div className="flex flex-col space-y-2 text-sm text-gray-700">
                    {rowData.type === "bank" ? (
                      <>
                        <div><span className="font-semibold">Name:</span> {rowData.details.accountName}</div>
                        <div><span className="font-semibold">Account:</span> {rowData.details.accountNumber}</div>
                        <div><span className="font-semibold">Bank:</span> {rowData.details.bankName}</div>
                      </>
                    ) : (
                      <>
                        <div><span className="font-semibold">Provider:</span> {rowData.details.walletProvider}</div>
                        <div><span className="font-semibold">Number:</span> {rowData.details.walletNumber}</div>
                      </>
                    )}
                  </div>
                )}
              />
          <Column
            header="Action"
            body={(_, { rowIndex }) => (
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-outlined p-button-sm"
                onClick={() => handleEdit(rowIndex)}
                tooltip="Edit"
                tooltipOptions={{ position: "left" }}
              />
            )}
            style={{ width: '100px', textAlign: 'center' }}
          />
    
          </DataTable>
            )}
          </Card>

          {/* Form */}
          <Card className="shadow-lg border border-gray-200">
            <h2 className="text-2xl font-semibold mb-6 text-blue-800">
              {editingIndex !== null ? "Edit Withdrawal account" : "Add Withdrawal account"}
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-700">Account Type *</label>
              <Dropdown
                value={selectedType}
                options={typeOptions}
                onChange={(e) => setSelectedType(e.value)}
                className="w-full border border-gray-300 rounded-lg h-12 pt-1"
                placeholder="Select type"
              />
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-4">
              {selectedType === "bank" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Account Holder Name *</label>
                    <InputText
                      name="accountName"
                      value={formik.values.accountName}
                      onChange={formik.handleChange}
                      className={`w-full h-12 rounded-lg border px-3 ${formik.errors.accountName ? "border-red-500" : "border-gray-300"}`}
                    />
                    {formik.errors.accountName && <Message severity="error" text={formik.errors.accountName} className="mt-1" />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Account Number *</label>
                    <InputText
                      name="accountNumber"
                      value={formik.values.accountNumber}
                      onChange={formik.handleChange}
                      className={`w-full h-12 rounded-lg border px-3 ${formik.errors.accountNumber ? "border-red-500" : "border-gray-300"}`}
                    />
                    {formik.errors.accountNumber && <Message severity="error" text={formik.errors.accountNumber} className="mt-1" />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Bank Name *</label>
                    <InputText
                      name="bankName"
                      value={formik.values.bankName}
                      onChange={formik.handleChange}
                      className={`w-full h-12 rounded-lg border px-3 ${formik.errors.bankName ? "border-red-500" : "border-gray-300"}`}
                    />
                    {formik.errors.bankName && <Message severity="error" text={formik.errors.bankName} className="mt-1" />}
                  </div>
                </>
              )}

              {selectedType === "wallet" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Wallet Provider *</label>
                    <InputText
                      name="walletProvider"
                      value={formik.values.walletProvider}
                      onChange={formik.handleChange}
                      className={`w-full h-12 rounded-lg border px-3 ${formik.errors.walletProvider ? "border-red-500" : "border-gray-300"}`}
                    />
                    {formik.errors.walletProvider && <Message severity="error" text={formik.errors.walletProvider} className="mt-1" />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">Wallet Number *</label>
                    <InputText
                      name="walletNumber"
                      value={formik.values.walletNumber}
                      onChange={formik.handleChange}
                      className={`w-full h-12 rounded-lg border px-3 ${formik.errors.walletNumber ? "border-red-500" : "border-gray-300"}`}
                    />
                    {formik.errors.walletNumber && <Message severity="error" text={formik.errors.walletNumber} className="mt-1" />}
                  </div>
                </>
              )}

              <div className="flex justify-end mt-6">
                <Button
                  type="submit"
                  label={editingIndex !== null ? "Update" : "Add"}
                  icon={editingIndex !== null ? "pi pi-refresh" : "pi pi-check"}
                  className="p-button-success px-5 py-2"
                  disabled={submitting}
                />
              </div>
            </form>
          </Card>
        </div>
      </div>
    );
  };

  export default AddWithdrawalInfo;
